/**
 * Color Drop Zone Element Grader
 */

import size from 'lodash/size';
import { createScoringEngine } from '@ai-content-hub/content-core';
import type { ScoringConfig } from '@ai-content-hub/content-core';
import type {
    ElementGrader,
    GradingConfig,
    GradingResult,
    ValidationResult,
    ValidationError,
} from '@ai-content-hub/content-kit';
import type {
    ColorDropZoneElementData,
    ColorDropZoneAnswer,
    DropZoneObject,
    DropZoneHotspot,
} from './schema';
import { getBackgroundImageSrc } from './schema';
import type { BlockContent } from '../block';

export const colorDropZoneGrader: ElementGrader<
    ColorDropZoneElementData,
    ColorDropZoneAnswer
> = {
    grade(
        data: ColorDropZoneElementData,
        answer: ColorDropZoneAnswer,
        config: GradingConfig = {}
    ): GradingResult {
        const { objects } = data;

        if (!objects || objects.length === 0) {
            return {
                isCorrect: false,
                score: 0,
                max: 0,
                feedback: 'No objects to grade.',
            };
        }

        const scoringConfig: ScoringConfig = {
            max: objects.length,
            partialCreditEnabled: config.partialCredit ?? true,
        };

        const engine = createScoringEngine(scoringConfig);
        let correctCount = 0;
        const itemResults: Record<string, { zoneCorrect: boolean; colorCorrect: boolean }> = {};

        const mode = data.mode ?? 'drag_and_color';
        const isColorOnlyMode = mode === 'color_only';

        for (const obj of objects) {
            const userAnswer = answer[obj.id];
            const zoneCorrect =
                isColorOnlyMode
                    ? true  // In color_only, objects are pre-placed; zone is always correct
                    : (!!userAnswer?.zoneId && userAnswer.zoneId === obj.correctZoneId);
            const colorCorrect =
                !!userAnswer?.colorId && userAnswer.colorId === obj.correctColorId;
            const bothCorrect = zoneCorrect && colorCorrect;
            if (bothCorrect) correctCount++;
            itemResults[obj.id] = { zoneCorrect, colorCorrect };
        }

        const scoreResult = engine.calculatePartialScore(correctCount, objects.length);

        return {
            ...scoreResult,
            correctAnswer: colorDropZoneGrader.getCorrectAnswer(data),
            feedback: scoreResult.isCorrect
                ? 'Correct! All placements and colors are correct.'
                : `You got ${correctCount} of ${objects.length} objects correct (zone + color).`,
            details: {
                correctCount,
                totalObjects: objects.length,
                totalItems: objects.length,
                itemResults,
            },
        };
    },

    getCorrectAnswer(data: ColorDropZoneElementData): ColorDropZoneAnswer {
        const answer: ColorDropZoneAnswer = {};
        for (const obj of data.objects ?? []) {
            answer[obj.id] = {
                zoneId: obj.correctZoneId,
                colorId: obj.correctColorId,
            };
        }
        return answer;
    },

    validate(data: ColorDropZoneElementData): ValidationResult {
        const errors: ValidationError[] = [];

        const bgSrc = getBackgroundImageSrc(data.background);
        if (!bgSrc || !bgSrc.trim()) {
            errors.push({
                field: 'background',
                message: 'Background image is required',
                code: 'required',
            });
        }

        if (!data.zones || size(data.zones) < 1) {
            errors.push({
                field: 'zones',
                message: 'At least 1 zone is required',
                code: 'minItems',
            });
        } else {
            data.zones.forEach((zone: DropZoneHotspot, index: number) => {
                if (!zone.label?.trim()) {
                    errors.push({
                        field: `zones.${index}.label`,
                        message: `Zone ${index + 1} label is required`,
                        code: 'required',
                    });
                }
                if (zone.radius < 20 || zone.radius > 200) {
                    errors.push({
                        field: `zones.${index}.radius`,
                        message: 'Zone radius must be between 20 and 200',
                        code: 'range',
                    });
                }
            });
        }

        if (!data.colorPalette || size(data.colorPalette) < 2) {
            errors.push({
                field: 'colorPalette',
                message: 'At least 2 colors are required in the palette',
                code: 'minItems',
            });
        }

        if (!data.objects || size(data.objects) < 1) {
            errors.push({
                field: 'objects',
                message: 'At least 1 object is required',
                code: 'minItems',
            });
        } else {
            data.objects.forEach((obj: DropZoneObject, index: number) => {
                if (!obj.name?.trim()) {
                    errors.push({
                        field: `objects.${index}.name`,
                        message: `Object ${index + 1} name is required`,
                        code: 'required',
                    });
                }
                const hasContent = hasBlockContent(obj.content);
                if (!hasContent) {
                    errors.push({
                        field: `objects.${index}.content`,
                        message: `Object ${index + 1} content (image) is required`,
                        code: 'required',
                    });
                }
                if (!obj.correctColorId) {
                    errors.push({
                        field: `objects.${index}.correctColorId`,
                        message: `Object ${index + 1} must have correct color selected`,
                        code: 'required',
                    });
                }
                if (!obj.correctZoneId) {
                    errors.push({
                        field: `objects.${index}.correctZoneId`,
                        message: `Object ${index + 1} must have correct zone selected`,
                        code: 'required',
                    });
                }
            });
        }

        return {
            isValid: size(errors) === 0,
            errors,
        };
    },

    /**
     * Each object is 1 gradable question.
     */
    countQuestions(data: ColorDropZoneElementData): number {
        return (data.objects ?? []).length;
    },
};

function hasBlockContent(content: BlockContent | undefined): boolean {
    if (!content || typeof content !== 'object') return false;
    if (content.type === 'image' && 'src' in content) {
        return !!(content as { src?: string }).src?.trim();
    }
    if (content.type === 'text' && 'text' in content) {
        return !!(content as { text?: string }).text?.trim();
    }
    if (content.type === 'audio' && 'src' in content) {
        return !!(content as { src?: string }).src?.trim();
    }
    if (content.type === 'video' && 'src' in content) {
        return !!(content as { src?: string }).src?.trim();
    }
    return false;
}

export default colorDropZoneGrader;
